import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { trackEvent, trackGoal, setCustomDimension, prepareWebAnalytics, MATOMO_DIMENSION } from '@/constants/utils/analytics';

/**
 * The single source of truth for Matomo event names.
 *
 * Every event goes out as `category / action / name / value`, where `name` is
 * always a slash-joined context path (`plus/whop/declined`) and `value` is a
 * number Matomo can sum — revenue for money events, a count otherwise. Reports
 * break the moment two surfaces spell the same thing differently, so nothing
 * should call `trackEvent` with a literal for anything listed here.
 *
 * See docs/ANALYTICS_EVENTS.md for the full taxonomy and the Matomo Goal setup
 * these names are expected to be wired to.
 */
export const ANALYTICS_CATEGORY = Object.freeze({
  AUTH: 'auth',
  ACTIVATION: 'activation',
  CHECKOUT: 'checkout',
  CONVERSION: 'conversion',
  MARKETING: 'marketing',
  RETENTION: 'retention',
  ACCOUNT: 'account',
});

export const AUTH_ACTION = Object.freeze({
  SIGNUP_SUCCESS: 'signup_success',
  SIGNUP_FAILED: 'signup_failed',
  LOGIN_SUCCESS: 'login_success',
});

export const ACTIVATION_ACTION = Object.freeze({
  FIRST_MESSAGE_SENT: 'first_message_sent',
  FIRST_RESPONSE_RECEIVED: 'first_response_received',
  FIRST_SUCCESSFUL_RESULT: 'first_successful_result',
});

export const CHECKOUT_ACTION = Object.freeze({
  STARTED: 'checkout_started',
  ABANDONED: 'checkout_abandoned',
  FAILED: 'checkout_failed',
  PURCHASE_COMPLETED: 'purchase_completed',
  PURCHASE_FAILED: 'purchase_failed',
});

export const CONVERSION_ACTION = Object.freeze({
  PAYWALL_SHOWN: 'paywall_shown',
  PAYWALL_UPGRADE_CLICKED: 'paywall_upgrade_clicked',
  PAYWALL_CREDITS_CLICKED: 'paywall_credits_clicked',
  PAYWALL_DISMISSED: 'paywall_dismissed',
  UPGRADE_BANNER_SHOWN: 'upgrade_banner_shown',
  UPGRADE_BANNER_CLICKED: 'upgrade_banner_clicked',
  CREDITS_DEPLETED_CLICKED: 'credits_depleted_clicked',
  LOW_CREDITS_BANNER_CLICKED: 'low_credits_banner_clicked',
});

export const MARKETING_ACTION = Object.freeze({
  CTA_CLICKED: 'cta_clicked',
});

export const RETENTION_ACTION = Object.freeze({
  CANCEL_FLOW_STARTED: 'cancel_flow_started',
  SAVE_OFFER_SHOWN: 'save_offer_shown',
  SAVE_OFFER_ACCEPTED: 'save_offer_accepted',
  SAVE_OFFER_DECLINED: 'save_offer_declined',
  SUBSCRIPTION_CANCELED: 'subscription_canceled',
  REFERRAL_SHARED: 'referral_shared',
  EMAIL_VERIFIED: 'email_verified',
  PHONE_VERIFIED: 'phone_verified',
});

/**
 * Matomo Goal IDs. Goals are created in the Matomo UI as manually triggered
 * (`trackGoal()`). See docs/ANALYTICS_EVENTS.md for the mapping.
 */
export const MATOMO_GOAL = Object.freeze({
  SIGNUP: 7,
  ACTIVATION: 8,
  CHECKOUT_STARTED: 9,
  PURCHASE: 10,
  SAVE_OFFER_ACCEPTED: 11,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build the `name` half of an event: a stable, slash-joined context path.
 * Empty segments are dropped so an optional reason never leaves a trailing
 * separator that would split one label into two in reports.
 */
export const eventName = (...segments) => segments
  .filter((segment) => segment != null && segment !== '')
  .map((segment) => String(segment).trim().toLowerCase().replace(/\s+/g, '_'))
  .filter(Boolean)
  .join('/');

// `Number(null)` is 0, so an absent value has to be rejected before coercion.
// Reporting a valueless event as zero would pull it into Matomo's event-value
// average and quietly understate revenue per event.
const numericValue = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const track = (category, action, name, value) => {
  if (!category || !action) return;
  trackEvent(category, action, name || null, numericValue(value));
};

/**
 * Goals fire from here rather than from each call site. A signup recorded from
 * the email form and one recorded from an SSO callback have to count as the
 * same conversion, and they only do if neither of them is responsible for
 * remembering to say so.
 */
export const trackAuthEvent = (action, name, value) => {
  track(ANALYTICS_CATEGORY.AUTH, action, name, value);
  if (action === AUTH_ACTION.SIGNUP_SUCCESS) trackGoal(MATOMO_GOAL.SIGNUP);
};

export const trackCheckoutEvent = (action, name, value) =>
  track(ANALYTICS_CATEGORY.CHECKOUT, action, name, value);

export const trackConversionEvent = (action, name, value) =>
  track(ANALYTICS_CATEGORY.CONVERSION, action, name, value);

export const trackMarketingEvent = (action, name, value) =>
  track(ANALYTICS_CATEGORY.MARKETING, action, name, value);

export const trackRetentionEvent = (action, name, value) =>
  track(ANALYTICS_CATEGORY.RETENTION, action, name, value);

// ---------------------------------------------------------------------------
// Once-per-user events
// ---------------------------------------------------------------------------

const ONCE_STORAGE_PREFIX = '@analytics_once:';
const firedOnce = new Set();

/**
 * Fire `emit` at most once for a given key, permanently.
 *
 * Activation milestones ("first message sent") are only meaningful as a count
 * of users, so re-firing them on every app launch would turn the metric into a
 * session count. The in-memory guard covers the concurrent case that storage
 * can't: two callers racing before either write lands.
 *
 * Storage failures fall back to the in-memory guard rather than skipping the
 * event — an over-count in one session beats never recording the milestone.
 */
export const trackOnce = async (key, emit) => {
  if (!key || typeof emit !== 'function') return false;
  if (firedOnce.has(key)) return false;
  firedOnce.add(key);

  const storageKey = `${ONCE_STORAGE_PREFIX}${key}`;
  try {
    const seen = await AsyncStorage.getItem(storageKey);
    if (seen) return false;
    await AsyncStorage.setItem(storageKey, '1');
  } catch (_) { /* fall through to the in-memory guard */ }

  try {
    emit();
  } catch (_) { /* analytics must never break a caller */ }
  return true;
};

// Exposed for tests; the guard is process-lifetime state otherwise.
export const __resetTrackOnceCache = () => firedOnce.clear();

/**
 * Activation milestones are per-account, not per-device: the same person on a
 * second device has already activated. Anonymous visitors are keyed by device
 * so a pre-signup first message is still counted exactly once.
 */
export const trackActivationOnce = (userId, action, name = null) => trackOnce(
  `${action}:${userId || 'anonymous'}`,
  () => {
    track(ANALYTICS_CATEGORY.ACTIVATION, action, name);
    // Sending the first message is the activation moment: everything before it
    // is a visitor, everything after it is a user of the product.
    if (action === ACTIVATION_ACTION.FIRST_MESSAGE_SENT) trackGoal(MATOMO_GOAL.ACTIVATION);
  },
);

// ---------------------------------------------------------------------------
// Revenue funnel
// ---------------------------------------------------------------------------

/**
 * Mirror one checkout-funnel event from `lib/checkoutTracking` into Matomo.
 *
 * The backend already records these against the user for cohort analysis; this
 * is the same event in the tool that can put it next to the campaign, landing
 * page and device that produced it. Takes the tracker's payload verbatim so the
 * two can never drift out of step.
 */
const CHECKOUT_EVENT_ACTIONS = Object.freeze({
  started: CHECKOUT_ACTION.STARTED,
  abandoned: CHECKOUT_ACTION.ABANDONED,
  failed: CHECKOUT_ACTION.FAILED,
});

export const trackCheckoutFunnelEvent = (payload) => {
  const action = CHECKOUT_EVENT_ACTIONS[payload?.event];
  if (!action) return;

  const { processor, checkoutType, reason } = payload;
  trackCheckoutEvent(action, eventName(checkoutType, processor, reason));

  if (action === CHECKOUT_ACTION.STARTED) {
    trackGoal(MATOMO_GOAL.CHECKOUT_STARTED);
  }
};

// A web purchase can report itself twice: the embed's own completion callback
// fires, and a 3D Secure buyer is separately redirected to /payment-successful.
// Both are legitimate signals of the same charge, and only one of them happens
// per purchase in the common case — but a buyer who completes the challenge and
// lands back on a still-mounted embed would be counted twice. The window is
// per-tab and short, so a genuine second purchase minutes later still counts.
const PURCHASE_DEDUPE_STORAGE_KEY = 'gab:analytics:last_purchase_at';
const PURCHASE_DEDUPE_WINDOW_MS = 5 * 60 * 1000;
let lastPurchaseTrackedAt = 0;

const readLastPurchaseTrackedAt = () => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return lastPurchaseTrackedAt;
  try {
    const stored = Number(window.sessionStorage?.getItem(PURCHASE_DEDUPE_STORAGE_KEY));
    return Number.isFinite(stored) ? Math.max(stored, lastPurchaseTrackedAt) : lastPurchaseTrackedAt;
  } catch (_) {
    return lastPurchaseTrackedAt;
  }
};

const writeLastPurchaseTrackedAt = (timestamp) => {
  lastPurchaseTrackedAt = timestamp;
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    window.sessionStorage?.setItem(PURCHASE_DEDUPE_STORAGE_KEY, String(timestamp));
  } catch (_) { /* private mode / storage disabled */ }
};

// Exposed for tests; the window is per-tab state otherwise.
export const __resetPurchaseDedupe = () => {
  lastPurchaseTrackedAt = 0;
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    window.sessionStorage?.removeItem(PURCHASE_DEDUPE_STORAGE_KEY);
  } catch (_) { /* ignore */ }
};

/**
 * A completed purchase. `value` is the amount charged when the client knows it,
 * which it often doesn't — the authoritative revenue figure is the ecommerce
 * order the backend posts from the processor webhook (backend/utils/matomo.js).
 * This event exists for the half the backend can't see: which campaign, landing
 * page and device produced the sale.
 */
export const trackPurchaseCompleted = ({ checkoutType, processor, value, currency } = {}) => {
  const now = Date.now();
  if (now - readLastPurchaseTrackedAt() < PURCHASE_DEDUPE_WINDOW_MS) return false;
  writeLastPurchaseTrackedAt(now);

  const revenue = numericValue(value);
  trackCheckoutEvent(
    CHECKOUT_ACTION.PURCHASE_COMPLETED,
    eventName(checkoutType, processor, currency),
    revenue,
  );
  trackGoal(MATOMO_GOAL.PURCHASE, revenue);
  return true;
};

export const trackPurchaseFailed = ({ checkoutType, processor, reason } = {}) => {
  trackCheckoutEvent(
    CHECKOUT_ACTION.PURCHASE_FAILED,
    eventName(checkoutType, processor, reason),
  );
};

// ---------------------------------------------------------------------------
// Visit dimensions
// ---------------------------------------------------------------------------

const resolveUserTier = (user) => {
  if (!user) return 'anonymous';
  if (user.flags?.isGhost) return 'ghost';
  if (user.flags?.isComped) return 'comped';
  if (user.flags?.isPlus) return 'plus';
  return 'free';
};

/**
 * Attach the durable visit dimensions every report segments on. Called once the
 * user payload lands (and again whenever their tier changes), so a visit that
 * upgrades mid-session is attributed to the tier it ended on.
 */
export const setUserAnalyticsDimensions = (user) => {
  setCustomDimension(MATOMO_DIMENSION.USER_TIER, resolveUserTier(user));
  setCustomDimension(MATOMO_DIMENSION.PLATFORM, Platform.OS);
};

export const setCheckoutSourceDimension = (source) => {
  setCustomDimension(MATOMO_DIMENSION.CHECKOUT_SOURCE, source);
};

/**
 * Prepare the same first-entry context as the public Pug shell. Re-mounting the
 * app must not replace that entry with /app or signup. Matomo's built-in entry
 * report and campaign/referrer attribution remain separate and unchanged.
 */
export const setLandingPathDimension = () => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    prepareWebAnalytics();
  } catch (_) { /* best effort */ }
};

export { resolveUserTier };
